import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Play, RefreshCw } from "lucide-react";

function AddBrandForm({ onDone }) {
  const [f, setF] = useState({ name: "", sector_code: "FASHION", domain: "", country: "IT" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await http.post("/admin/pipeline/add-brand", f);
      toast.success(`Brand queued (run ${data.run_id.slice(0, 8)}…)`);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 text-sm">
      <div><label className="text-xs uppercase tracking-widest">Brand name</label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pipeline-add-name" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest">Sector</label>
          <select value={f.sector_code} onChange={(e) => setF({ ...f, sector_code: e.target.value })} className="w-full border p-2 bg-white" style={{ borderColor: "var(--bp-border)" }} data-testid="pipeline-add-sector">
            <option>FASHION</option><option>FURNITURE</option>
          </select>
        </div>
        <div><label className="text-xs uppercase tracking-widest">Country (ISO2)</label><Input maxLength={2} value={f.country} onChange={(e) => setF({ ...f, country: e.target.value.toUpperCase() })} data-testid="pipeline-add-country" /></div>
      </div>
      <div><label className="text-xs uppercase tracking-widest">Official domain (optional)</label><Input value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} placeholder="brand.com" data-testid="pipeline-add-domain" /></div>
      <Button type="submit" disabled={busy} className="rounded-none w-full" data-testid="pipeline-add-submit">{busy ? "Queuing…" : "Add & start pipeline"}</Button>
    </form>
  );
}

const STATE_COLOR = {
  QUEUED: "text-neutral-500", RUNNING: "text-blue-700", SUCCEEDED: "text-green-800",
  AWAITING_APPROVAL: "text-amber-700", HELD: "text-amber-700", FAILED: "text-red-700",
};

export default function AdminPipeline() {
  const [runs, setRuns] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // { runIds, done, total, label }
  const load = useCallback(() => http.get("/admin/pipeline/runs?limit=100").then((r) => setRuns(r.data.runs || [])), []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  // Bulk-progress poller — reuses the pattern already in place for the
  // recrawl toast on the Ops page. Ends when every run reaches a
  // terminal state.
  useEffect(() => {
    if (!bulkProgress || bulkProgress.done >= bulkProgress.total) return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await http.get(
          `/admin/pipeline/runs-status?ids=${bulkProgress.runIds.join(",")}`,
        );
        const TERMINAL = new Set(["SUCCEEDED", "FAILED", "NEEDS_ATTENTION", "AWAITING_APPROVAL", "HELD", "PUBLISHED_ACCESS_RESTRICTED"]);
        const done = (data.runs || []).filter((r) => TERMINAL.has(r.state)).length;
        setBulkProgress((p) => (p ? { ...p, done } : p));
      } catch { /* transient */ }
    }, 4000);
    return () => clearInterval(timer);
  }, [bulkProgress]);

  const reprocess = async (brandId) => {
    try { await http.post(`/admin/pipeline/reprocess/${brandId}`); toast.success("Re-queued"); load(); }
    catch (e) {
      if (e.response?.status === 409) {
        const d = e.response.data?.detail;
        toast.message(typeof d === "object" ? (d.message || "Run already in flight") : "Run already in flight");
      } else {
        toast.error("Failed");
      }
    }
  };

  const bulkReprocessNeedsAttention = async () => {
    const needsAttentionCount = runs.filter((r) => r.state === "NEEDS_ATTENTION").length;
    if (needsAttentionCount === 0) {
      toast.message("No NEEDS_ATTENTION runs to reprocess.");
      return;
    }
    if (!window.confirm(`Re-queue every brand whose latest run is NEEDS_ATTENTION? Currently showing ${needsAttentionCount} in the list — the actual count queried server-side may be higher.`)) return;
    try {
      const { data } = await http.post("/admin/pipeline/bulk-reprocess-needs-attention");
      toast.success(`Re-queued ${data.count} brand(s)`);
      if (data.count > 0) {
        setBulkProgress({
          runIds: (data.enqueued || []).map((e) => e.run_id).filter(Boolean),
          done: 0,
          total: data.count,
          label: "Reprocess NEEDS_ATTENTION",
        });
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <AdminLayout>
      <div className="bp-container py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif text-4xl">Pipeline monitor</h1>
          <div className="flex items-center gap-2">
            <Button
              onClick={bulkReprocessNeedsAttention}
              variant="outline"
              className="rounded-none"
              data-testid="bulk-reprocess-needs-attention-btn"
            >
              Reprocess all NEEDS_ATTENTION →
            </Button>
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild><Button className="rounded-none" data-testid="pipeline-open-add">+ Add brand to pipeline</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif">Add brand to pipeline</DialogTitle></DialogHeader>
              <AddBrandForm onDone={() => { setShowAdd(false); load(); }} />
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Started</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Brand</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Kind</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Stage</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">State</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Outcome</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`run-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{(r.started_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3">
                    <div>{r.brand?.display_name || "—"}</div>
                    <div className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>{r.brand?.slug} · {r.brand?.sector_code}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{r.kind}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.stage}</td>
                  <td className={`px-4 py-3 font-mono text-xs ${STATE_COLOR[r.state] || ""}`}>{r.state}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.outcome || (r.held_reason ? `HELD: ${r.held_reason}` : "—")}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {r.state === "AWAITING_APPROVAL" && r.approval_task_id && (
                      <Link
                        to={`/admin/approvals?task=${r.approval_task_id}`}
                        className="bp-link text-sm"
                        data-testid={`approve-deeplink-${r.id}`}
                      >
                        Approve →
                      </Link>
                    )}
                    {r.state === "AWAITING_APPROVAL" && !r.approval_task_id && (
                      <Link to="/admin/approvals" className="bp-link text-sm">Approve →</Link>
                    )}
                    <button onClick={() => reprocess(r.brand_id)} className="text-xs underline inline-flex items-center gap-1" data-testid={`reprocess-${r.brand_id}`}><RefreshCw size={12} /> Reprocess</button>
                  </td>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center" style={{ color: "var(--bp-text-muted)" }}>No runs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk reprocess progress toast — fixed bottom-right, polls the
          batch until every run reaches a terminal state. */}
      {bulkProgress && (
        <div
          className="fixed bottom-6 right-6 z-50 shadow-lg border p-4"
          style={{ background: "white", borderColor: "var(--bp-border)", minWidth: 260, maxWidth: 360 }}
          data-testid="bulk-reprocess-progress-toast"
        >
          <div className="flex items-baseline justify-between mb-2">
            <div className="uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
              {bulkProgress.label}
            </div>
            <button
              onClick={() => setBulkProgress(null)}
              className="text-xs bp-link"
              title="Dismiss"
              data-testid="bulk-reprocess-dismiss"
            >
              ✕
            </button>
          </div>
          <div className="text-sm font-mono mb-2" data-testid="bulk-reprocess-count">
            {bulkProgress.done}/{bulkProgress.total} completed
          </div>
          <div className="h-1.5 w-full" style={{ background: "#EEE" }}>
            <div
              className="h-full transition-all"
              style={{
                width: `${Math.round((bulkProgress.done / Math.max(1, bulkProgress.total)) * 100)}%`,
                background: bulkProgress.done >= bulkProgress.total ? "#166534" : "#0369A1",
              }}
            />
          </div>
          {bulkProgress.done >= bulkProgress.total && (
            <div className="mt-2 text-xs" style={{ color: "#166534" }}>
              All runs terminal. Reload to review outcomes.
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}
